import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Button, IconButton, Modal, TextField } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectCurrentTrip } from '../../../redux/trip/trip.selectors';
import { updateCard } from '../../../redux/trip/trip.slice';
import type { CurrentTrip, TripCard } from '../../../redux/trip/trip.types';
import './CardTagsModal.scss';

type CardTagsModalProps = {
  card: TripCard;
  tripId: string;
  open: boolean;
  onClose: () => void;
};

// Constants
const DEFAULT_TAGS = [
  'Good for Lunch',
  'Need Reservation',
  'Cash Only',
  'Booked',
  'Good for Groups',
  'Pet Friendly',
] as const;

const MAX_TAG_LENGTH = 25;
const MAX_TAGS_PER_CARD = 5;

// Utility functions
const getUniqueTripsTagsExcludingCard = (
  currentTrip: CurrentTrip | null | undefined,
  cardId: string
): string[] => {
  if (!currentTrip) {
    return [];
  }

  const allTags = Object.values(currentTrip.cards)
    .filter((c: TripCard) => c.id !== cardId)
    .flatMap((c: TripCard) => c.tags || []);

  const lowerCaseTags = allTags.map((tag: string) => tag.toLowerCase());
  const uniqueLowerTags = Array.from(new Set(lowerCaseTags));

  return uniqueLowerTags
    .map((lowerTag) => allTags.find((tag: string) => tag.toLowerCase() === lowerTag) || lowerTag)
    .sort();
};

function CardTagsModal({ card, tripId, open, onClose }: CardTagsModalProps) {
  const dispatch = useAppDispatch();
  const currentTrip = useAppSelector(selectCurrentTrip);
  const isMobile = useMobileMediaQuery();

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  /** useMemo stabilises the array reference — without it,`card.tags || []` creates a new [] on every render when tags is undefined, causing the useEffect below (which syncs localTags) to fire continuously and exceed the max update depth. */
  const currentTags = useMemo(() => card.tags || [], [card.tags]);

  // Drag-to-reorder state
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [localTags, setLocalTags] = useState<string[]>(currentTags);
  const draggingIndexRef = useRef<number | null>(null);
  const localTagsRef = useRef<string[]>(currentTags);
  const tagDivRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (draggingIndexRef.current === null) {
      localTagsRef.current = currentTags;
      setLocalTags(currentTags);
    }
  }, [currentTags]);

  const tripTags = useMemo(
    () => getUniqueTripsTagsExcludingCard(currentTrip, card.id),
    [currentTrip, card.id]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.length > MAX_TAG_LENGTH) {
      setError(`Tag must be ${MAX_TAG_LENGTH} characters or less`);
    } else {
      setError('');
    }
  };

  const updateCardTags = (newTags: string[]) => {
    dispatch(
      updateCard({
        tripId,
        cardId: card.id,
        field: 'tags',
        value: newTags,
      })
    );
  };

  const validateAndGetError = (value: string): string => {
    if (value.length > MAX_TAG_LENGTH) {
      return `Tag must be ${MAX_TAG_LENGTH} characters or less`;
    }

    if (currentTags.length >= MAX_TAGS_PER_CARD) {
      return `Maximum ${MAX_TAGS_PER_CARD} tags allowed per card`;
    }

    const isDuplicate = currentTags.some((tag) => tag.toLowerCase() === value.toLowerCase());

    if (isDuplicate) {
      return 'Tag already exists on this card';
    }

    return '';
  };

  const handleAddTag = () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      return;
    }

    const validationError = validateAndGetError(trimmedValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    updateCardTags([...currentTags, trimmedValue]);
    setInputValue('');
    setError('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    updateCardTags(currentTags.filter((tag) => tag !== tagToRemove));
  };

  const handleSuggestionClick = (suggestionTag: string) => {
    const validationError = validateAndGetError(suggestionTag);
    if (validationError) {
      setError(validationError);
      return;
    }

    updateCardTags([...currentTags, suggestionTag]);
    setError('');
  };

  // Immediately capture the pointer on the drag handle so all subsequent
  // move/up events are routed to the parent tag div, even as the cursor leaves it.
  const handleDragHandlePointerDown = (index: number, e: React.PointerEvent) => {
    e.preventDefault();
    const tagDiv = tagDivRefs.current[index];
    if (!tagDiv) {
      return;
    }
    tagDiv.setPointerCapture(e.pointerId);
    draggingIndexRef.current = index;
    setDraggingIndex(index);
  };

  // Reorder by checking which tag div the pointer is currently over using bounding rects.
  // elementsFromPoint is avoided here because pointer capture interferes with it.
  const handleTagPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (draggingIndexRef.current === null) {
      return;
    }
    const fromIndex = draggingIndexRef.current;

    for (let i = 0; i < tagDivRefs.current.length; i++) {
      if (i === fromIndex) {
        continue;
      }
      const el = tagDivRefs.current[i];
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      if (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      ) {
        const newTags = [...localTagsRef.current];
        const [item] = newTags.splice(fromIndex, 1);
        newTags.splice(i, 0, item);
        localTagsRef.current = newTags;
        setLocalTags(newTags);
        draggingIndexRef.current = i;
        setDraggingIndex(i);
        break;
      }
    }
  };

  // Commit the reordered tags to Firebase on drop.
  const handleTagPointerUp = () => {
    if (draggingIndexRef.current !== null) {
      updateCardTags(localTagsRef.current);
      draggingIndexRef.current = null;
      setDraggingIndex(null);
    }
  };

  // Restore original order if the drag is interrupted (e.g. touch cancelled).
  const handleTagPointerCancel = () => {
    if (draggingIndexRef.current !== null) {
      draggingIndexRef.current = null;
      localTagsRef.current = currentTags;
      setLocalTags([...currentTags]);
      setDraggingIndex(null);
    }
  };

  const handleClose = () => {
    setInputValue('');
    setError('');
    onClose();
  };

  const isTagDisabled = (tag: string) => {
    return (
      currentTags.some((currentTag) => currentTag.toLowerCase() === tag.toLowerCase()) ||
      currentTags.length >= MAX_TAGS_PER_CARD
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
    >
      <div
        className='modal-container'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()} // Prevent dnd-kit listeners on parent from intercepting
      >
        <div className='modal-header'>
          {isMobile ? (
            <Button
              className='mobile-back-btn'
              startIcon={<ArrowBackIcon className='mobile-back-icon' />}
              onClick={handleClose}
            />
          ) : (
            <>
              <div>
                <h2 className='modal-title'>Edit Tags:</h2>
                <span className='modal-subtitle'>(Add up to 5)</span>
              </div>
              <Button
                className='header-btn'
                onClick={handleClose}
                color='inherit'
              >
                ×
              </Button>
            </>
          )}
        </div>

        <div className='modal-content'>
          <div className='input-section'>
            <div className='input-container'>
              <div className='input-wrapper'>
                <TextField
                  className='tag-input'
                  label='Add a custom tag...'
                  variant='standard'
                  value={inputValue}
                  onChange={handleInputChange}
                  inputProps={{ maxLength: MAX_TAG_LENGTH }}
                />
                {inputValue && (
                  <IconButton
                    className='clear-btn'
                    onClick={() => setInputValue('')}
                    size='small'
                    aria-label='clear input'
                  >
                    <ClearIcon />
                  </IconButton>
                )}
                <div className='character-counter'>
                  <span>
                    {inputValue.length}/{MAX_TAG_LENGTH}
                  </span>
                </div>
              </div>
              <Button
                className='button add-btn'
                variant='contained'
                onClick={handleAddTag}
                disabled={!inputValue.trim() || inputValue.length > MAX_TAG_LENGTH}
              >
                Add
              </Button>
            </div>
            {error && <div className='error-msg'>{error}</div>}
          </div>

          {localTags.length > 0 && (
            <div className='section'>
              <div className='tags-grid'>
                {localTags.map((tag, index) => (
                  <div
                    ref={(el) => {
                      tagDivRefs.current[index] = el;
                    }}
                    className={`current-tag${draggingIndex === index ? ' dragging' : ''}`}
                    key={tag}
                    onPointerMove={handleTagPointerMove}
                    onPointerUp={handleTagPointerUp}
                    onPointerCancel={handleTagPointerCancel}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <DragIndicatorIcon
                      className='drag-handle'
                      onPointerDown={(e) => handleDragHandlePointerDown(index, e)}
                    />
                    {tag}
                    <button
                      type='button'
                      className='remove-tag-btn'
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTag(tag);
                      }}
                      aria-label={`Remove ${tag} tag`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='section'>
            <div className='section-title'>Select from quick tags:</div>
            <div className='tags-grid'>
              {DEFAULT_TAGS.map((tag) => (
                <button
                  type='button'
                  className='suggestion-tag'
                  key={tag}
                  onClick={() => handleSuggestionClick(tag)}
                  disabled={isTagDisabled(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {tripTags.length > 0 && (
            <div className='section'>
              <div className='section-title'>Select from existing tags in this trip:</div>
              <div className='tags-grid'>
                {tripTags.map((tag) => (
                  <button
                    type='button'
                    className='suggestion-tag'
                    key={tag}
                    onClick={() => handleSuggestionClick(tag)}
                    disabled={isTagDisabled(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

CardTagsModal.displayName = 'CardTagsModal';
export default CardTagsModal;
